import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { createNanoEvents } from 'nanoevents';
import { uniqueId, invariant, ErrorConstant, isFunction, isString, isDomNode, isElementDomNode, isArray, includes, isNodeOfType, isPlainObject, entries, SELECTED_NODE_CLASS_NAME, extensionDecorator, PlainExtension, object, isNodeExtension } from '@remirror/core';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useState, useCallback, useEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';

/**
 * The node view portal container keeps track of all the portals which have been added by react to render
 * the node views in the editor.
 */
class PortalContainer {
  constructor() {
    _defineProperty(this, "portals", new Map());

    _defineProperty(this, "events", createNanoEvents());

    _defineProperty(this, "on", callback => {
      return this.events.on('update', callback);
    });

    _defineProperty(this, "once", callback => {
      var unbind = this.events.on('update', portalMap => {
        unbind();
        callback(portalMap);
      });
      return unbind;
    });
  }

  /**
   * Trigger an update in all subscribers.
   */
  update() {
    this.events.emit('update', this.portals);
  }
  /**
   * Responsible for registering a new portal by rendering the react element into the provided container.
   */


  render(_ref) {
    var Component = _ref.Component,
        container = _ref.container;
    var portal = this.portals.get(container);
    var key = portal ? portal.key : uniqueId();
    this.portals.set(container, {
      Component,
      key
    });
    this.update();
  }
  /**
   * Force an update in all the portals by setting new keys for every portal.
   *
   * Delete all orphaned containers (deleted from the DOM). This is useful for
   * Decoration where there is no destroy method.
   */


  forceUpdate() {
    this.portals.forEach((_ref2, container) => {
      var Component = _ref2.Component;
      // Assign the portal a new key so it is re-rendered
      this.portals.set(container, {
        Component,
        key: uniqueId()
      });
    });
    this.update();
  }
  /**
   * Deletes the portal within the container.
   */


  remove(container) {
    this.portals.delete(container);
    this.update();
  }

}

/**
 * The component that places all the portals into the DOM.
 *
 * Portals can be created by NodeView and also the static
 * widget method on Decorations.
 */
var RemirrorPortals = (_ref) => {
  var portalContainer = _ref.portalContainer;

  var _useState = useState([...portalContainer.portals.entries()]),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];
  /**
   * Update the state whenever the portal is updated.
   */


  var onPortalChange = useCallback(portalMap => {
    setState([...portalMap.entries()]);
  }, []);
  useEffect(() => {
    // Auto disposed when the component un-mounts
    return portalContainer.on(onPortalChange);
  }, [onPortalChange, portalContainer]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, state.map((_ref2) => {
    var _ref3 = _slicedToArray(_ref2, 2),
        container = _ref3[0],
        _ref3$ = _ref3[1],
        Component = _ref3$.Component,
        key = _ref3$.key;

    return /*#__PURE__*/React.createElement(Fragment, {
      key: key
    }, /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(Portal, {
      container: container,
      Component: Component,
      portalContainer: portalContainer
    }), container));
  }));
};

/**
 * This is the component rendered by the createPortal method within the
 * RemirrorPortals component. It is responsible for cleanup when the container
 * is removed from the DOM.
 */
var Portal = props => {
  var portalContainer = props.portalContainer,
      container = props.container,
      Component = props.Component;
  useEffect(() => {
    /**
     * Remove the portal container entry when this portal is unmounted.
     * Portals are unmounted when their host container is removed from the dom.
     */
    return () => {
      portalContainer.remove(container);
    };
  }, [container, portalContainer]);
  return /*#__PURE__*/React.createElement(Component, null);
};

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var _node = /*#__PURE__*/new WeakMap();

var _decorations = /*#__PURE__*/new WeakMap();

var _view = /*#__PURE__*/new WeakMap();

var _portalContainer = /*#__PURE__*/new WeakMap();

var _extension = /*#__PURE__*/new WeakMap();

var _getPosition = /*#__PURE__*/new WeakMap();

var _options = /*#__PURE__*/new WeakMap();

var _selected = /*#__PURE__*/new WeakMap();

var _contentDOM = /*#__PURE__*/new WeakMap();

var _dom = /*#__PURE__*/new WeakMap();

var _forwardRef = /*#__PURE__*/new WeakMap();

class ReactNodeView {
  /**
   * A shorthand method for creating the ReactNodeView
   */
  static create(parameter) {
    var portalContainer = parameter.portalContainer,
        extension = parameter.extension,
        options = parameter.options;
    return (node, view, getPosition) => new ReactNodeView({
      options,
      node,
      view,
      getPosition,
      portalContainer,
      extension
    });
  }
  /**
   * The `ProsemirrorNode` that this nodeView is responsible for rendering.
   */


  /**
   * Whether or not the node is currently selected.
   */
  get selected() {
    return _classPrivateFieldGet(this, _selected);
  }

  /**
   * The DOM node that should hold the node's content. Only meaningful if the
   * node view also defines a `dom` property and if its node type is not a leaf
   * node type. When this is present, ProseMirror will take care of rendering
   * the node's children into it. When it is not present, the node view itself
   * is responsible for rendering (or deciding not to render) its child nodes.
   */
  get contentDOM() {
    return _classPrivateFieldGet(this, _contentDOM);
  }

  /**
   * Provides readonly access to the dom element. The dom is automatically for
   * react components.
   */
  get dom() {
    return _classPrivateFieldGet(this, _dom);
  }
  /**
   * Create the node view for a react component and render it into the dom.
   */


  constructor(_ref) {
    var getPosition = _ref.getPosition,
        _node2 = _ref.node,
        portalContainer = _ref.portalContainer,
        view = _ref.view,
        extension = _ref.extension,
        options = _ref.options;

    _node.set(this, {
      writable: true,
      value: void 0
    });

    _decorations.set(this, {
      writable: true,
      value: []
    });

    _view.set(this, {
      writable: true,
      value: void 0
    });

    _portalContainer.set(this, {
      writable: true,
      value: void 0
    });

    _extension.set(this, {
      writable: true,
      value: void 0
    });

    _getPosition.set(this, {
      writable: true,
      value: void 0
    });

    _options.set(this, {
      writable: true,
      value: void 0
    });

    _selected.set(this, {
      writable: true,
      value: false
    });

    _contentDOM.set(this, {
      writable: true,
      value: void 0
    });

    _dom.set(this, {
      writable: true,
      value: void 0
    });

    _forwardRef.set(this, {
      writable: true,
      value: node => {
        if (!node) {
          return;
        }

        !_classPrivateFieldGet(this, _contentDOM) ? process.env.NODE_ENV !== "production" ? invariant(false, {
          code: ErrorConstant.REACT_NODE_VIEW,
          message: "You have applied a ref to a nodeView provided by '".concat(_classPrivateFieldGet(this, _extension).constructor.name, "' which doesn't support content.")
        }) : invariant(false) : void 0;
        node.append(_classPrivateFieldGet(this, _contentDOM));
      }
    });

    _defineProperty(this, "Component", () => {
      var _classPrivateFieldGet2 = _classPrivateFieldGet(this, _extension),
          ReactComponent = _classPrivateFieldGet2.ReactComponent;

      !ReactComponent ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.REACT_NODE_VIEW,
        message: "The extension used to create this node view doesn't have a valid ReactComponent"
      }) : invariant(false) : void 0;
      return /*#__PURE__*/React.createElement(ReactComponent, {
        environment: "dom",
        updateAttributes: this.updateAttributes,
        selected: this.selected,
        view: _classPrivateFieldGet(this, _view),
        getPosition: _classPrivateFieldGet(this, _getPosition),
        node: _classPrivateFieldGet(this, _node),
        forwardRef: _classPrivateFieldGet(this, _forwardRef),
        options: _classPrivateFieldGet(this, _extension).options,
        decorations: _classPrivateFieldGet(this, _decorations)
      });
    });

    _defineProperty(this, "updateAttributes", attrs => {
      if (!_classPrivateFieldGet(this, _view).editable) {
        return;
      }

      var tr = _classPrivateFieldGet(this, _view).state.tr.setNodeMarkup(_classPrivateFieldGet(this, _getPosition).call(this), undefined, _objectSpread(_objectSpread({}, _classPrivateFieldGet(this, _node).attrs), attrs));

      _classPrivateFieldGet(this, _view).dispatch(tr);
    });

    !isFunction(getPosition) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      message: 'You are attempting to use a node view for a mark type. Please check your configuration.'
    }) : invariant(false) : void 0;

    _classPrivateFieldSet(this, _node, _node2);

    _classPrivateFieldSet(this, _view, view);

    _classPrivateFieldSet(this, _portalContainer, portalContainer);

    _classPrivateFieldSet(this, _extension, extension);

    _classPrivateFieldSet(this, _getPosition, getPosition);

    _classPrivateFieldSet(this, _options, options);

    _classPrivateFieldSet(this, _dom, this.createDom());

    _classPrivateFieldSet(this, _contentDOM, this.createContentDom());

    this.setDomAttributes(_classPrivateFieldGet(this, _node), _classPrivateFieldGet(this, _dom));
    this.Component.displayName = extension.constructor.name.replace('Extension', 'NodeView');
    this.renderComponent();
  }
  /**
   * Render the react component into the dom.
   */


  renderComponent() {
    _classPrivateFieldGet(this, _portalContainer).render({
      Component: this.Component,
      container: _classPrivateFieldGet(this, _dom)
    });
  }
  /**
   * Create the dom element which will hold the react component.
   */


  createDom() {
    var toDOM = _classPrivateFieldGet(this, _node).type.spec.toDOM;

    var _classPrivateFieldGet3 = _classPrivateFieldGet(this, _options),
        defaultBlockNode = _classPrivateFieldGet3.defaultBlockNode,
        defaultInlineNode = _classPrivateFieldGet3.defaultInlineNode;

    if (!toDOM) {
      return _classPrivateFieldGet(this, _node).isInline ? document.createElement(defaultInlineNode) : document.createElement(defaultBlockNode);
    }

    var domSpec = toDOM(_classPrivateFieldGet(this, _node));

    if (isString(domSpec)) {
      return document.createElement(domSpec);
    }

    if (isDomNode(domSpec)) {
      if (!isElementDomNode(domSpec)) {
        throw new Error('Invalid HTML Element provided in the DOM Spec');
      }

      return domSpec;
    } // Use the outer element string to render the dom node


    return document.createElement(domSpec[0]);
  }
  /**
   * The element that will contain the content for this element.
   */


  createContentDom() {
    var _classPrivateFieldGet4, _classPrivateFieldGet5;

    if (_classPrivateFieldGet(this, _node).isLeaf) {
      return;
    }

    var domSpec = (_classPrivateFieldGet4 = (_classPrivateFieldGet5 = _classPrivateFieldGet(this, _node).type.spec).toDOM) === null || _classPrivateFieldGet4 === void 0 ? void 0 : _classPrivateFieldGet4.call(_classPrivateFieldGet5, _classPrivateFieldGet(this, _node)); // If no content hole included return undefined.

    if (!(isArray(domSpec) && includes(domSpec, 0))) {
      return;
    }

    var element = document.createElement(_classPrivateFieldGet(this, _options).defaultContentNode);
    element.setAttribute('contenteditable', "".concat(_classPrivateFieldGet(this, _view).editable));
    return element;
  }
  /**
   * Adds a ref to the component that has been provided and can be used to set
   * it as the content container. However it is advisable to either not use
   * ReactNodeViews for nodes with content or to take control of rendering the
   * content within the component..
   */


  /**
   * Update the prosemirror node.
   */
  update(node, decorations) {
    if (!isNodeOfType({
      types: _classPrivateFieldGet(this, _node).type,
      node
    })) {
      return false;
    }

    if (_classPrivateFieldGet(this, _node) === node && _classPrivateFieldGet(this, _decorations) === decorations) {
      return true;
    }

    if (!_classPrivateFieldGet(this, _node).sameMarkup(node)) {
      this.setDomAttributes(node, _classPrivateFieldGet(this, _dom));
    }

    _classPrivateFieldSet(this, _node, node);

    _classPrivateFieldSet(this, _decorations, decorations);

    this.renderComponent();
    return true;
  }
  /**
   * Copies the attributes from a ProseMirror Node to the parent DOM node.
   *
   * @param node The Prosemirror Node from which to source the attributes
   */


  setDomAttributes(node, element) {
    var toDOM = _classPrivateFieldGet(this, _node).type.spec.toDOM;

    var attributes = node.attrs;

    if (toDOM) {
      var domSpec = toDOM(node);

      if (isString(domSpec) || isDomNode(domSpec)) {
        return;
      }

      if (isPlainObject(domSpec[1])) {
        attributes = domSpec[1];
      }
    }

    var _iterator = _createForOfIteratorHelper(entries(attributes)),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _step$value = _slicedToArray(_step.value, 2),
            attribute = _step$value[0],
            value = _step$value[1];

        element.setAttribute(attribute, value);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }
  /**
   * Marks the node as being selected.
   */


  selectNode() {
    _classPrivateFieldSet(this, _selected, true);

    if (_classPrivateFieldGet(this, _dom)) {
      _classPrivateFieldGet(this, _dom).classList.add(SELECTED_NODE_CLASS_NAME);
    }

    this.renderComponent();
  } // Remove selected node marking from this node.


  deselectNode() {
    _classPrivateFieldSet(this, _selected, false);

    if (_classPrivateFieldGet(this, _dom)) {
      _classPrivateFieldGet(this, _dom).classList.remove(SELECTED_NODE_CLASS_NAME);
    }

    this.renderComponent();
  }
  /**
   * This is called whenever the node is being destroyed.
   */


  destroy() {
    _classPrivateFieldGet(this, _portalContainer).remove(_classPrivateFieldGet(this, _dom));
  }

  ignoreMutation(mutation) {
    if (mutation.type === 'selection') {
      return false;
    }

    if (!this.contentDOM) {
      return true;
    }

    return !this.contentDOM.contains(mutation.target);
  }

}

var _dec, _class, _portalContainer$1, _temp;

function _createForOfIteratorHelper$1(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray$1(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
/**
 * The extension transforms the `Component` property on extensions into a
 *
 * - Valid nodeView wrapped in a wrapper dom element
 * - A valid SSR component.
 *
 * Currently this only support nodes. Support will be added for marks later.
 *
 * @remarks
 *
 * When creating a nodeView using the component property the `toDOM` method
 * returned by the `createNodeSpec` methods needs to be in the following format.
 *
 * - `string` - e.g. `div`. This will be used as the wrapper tag name. .
 * - `[string, 0]` - The wrapper tag name and a `0` indicating that this will be
 *   accepting content.
 * - `[string, object, 0?]` -The wrapper tag name, an object of the attributes
 *   that should be applied to the wrapper tag and a 0 when you want the react
 *   component to have content inserted into it.
 *
 * Unfortunately `React Components` currently require a wrapping tag element
 * when being used in the DOM. See the following for the reasons.
 *
 * ### Caveats
 *
 * It's not possible not to have the nesting in `React` due to this issue
 * https://github.com/facebook/react/issues/12227. Also, it's unlikely that any
 * changes will be made in ProseMirror, which makes a lot of sense
 * https://github.com/ProseMirror/prosemirror/issues/803
 *
 * NodeViews have a `dom` node which is the top element. For paragraphs this
 * would be the `p` tag and for text this is a `TEXT` node. NodeView's  also
 * have a `contentDOM` property which is where any content from ProseMirror is
 * injected.
 *
 * The difficulty in integration is that the dom node and the content dom node
 * of the `NodeView` are consumed synchronously by ProseMirror. However, react
 * requires a ref to determine where the node has been mounted and this is done
 * asynchronously. As a result it's not possible provide the `dom` node or
 * `contentDOM` to ProseMirror while using react.
 *
 * The only way around this, is to create both the top level dom node and the
 * content dom manually in the NodeView and provide a ref to the component which
 * should be attached to the part of the tree where content should be rendered
 * to. Once the React ref is available asynchronously the `contentDOM` can be
 * appended to it.
 *
 * I'm currently improving the API in the next branch and I'll update docs on
 * how it should be used once done.
 */

var ReactComponentExtension = (_dec = /*#__PURE__*/extensionDecorator({
  defaultOptions: {
    defaultBlockNode: 'div',
    defaultInlineNode: 'span',
    defaultContentNode: 'span',
    defaultEnvironment: 'both'
  },
  staticKeys: ['defaultEnvironment']
}), /*#__PURE__*/_dec(_class = (_temp = (_portalContainer$1 = /*#__PURE__*/new WeakMap(), class ReactComponentExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _portalContainer$1.set(this, {
      writable: true,
      value: new PortalContainer()
    });

    _defineProperty(this, "onCreate", () => {
      this.store.setStoreKey('portalContainer', _classPrivateFieldGet(this, _portalContainer$1));
    });

    _defineProperty(this, "createNodeViews", () => {
      var nodeViews = object();

      var _iterator = _createForOfIteratorHelper$1(this.store.extensions),
          _step;

      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var extension = _step.value;

          if (!extension.ReactComponent || !isNodeExtension(extension) || extension.reactComponentEnvironment === 'ssr') {
            continue;
          }

          nodeViews[extension.name] = ReactNodeView.create({
            options: this.options,
            extension,
            portalContainer: _classPrivateFieldGet(this, _portalContainer$1)
          });
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }

      return nodeViews;
    });
  }

  get name() {
    return 'reactNodeView';
  }
  /**
   * Add the portal container to the manager store. This can be used by the
   * `ReactEditor` to manage the portals.
   */


}), _temp)) || _class);

export { PortalContainer, ReactComponentExtension, RemirrorPortals };
